// The close sheet's decisions, kept out of the DOM so they can be tested.
//
// Lifted out of the Andon app's `CloseCard`, where they were inline between
// the hooks and the JSX. Three of them: what the close is still waiting for,
// which cause classes to put in front of this write-up, and the delay to show
// while somebody is nudging a stamp. And one that is new to the merge: whether
// to offer the quality alert after the close.

import type { AlertTypeKey, AndonAlertVM, CauseClass, CausesVM } from '@/api/andon/types';

/**
 * WHAT THE CLOSE WAITS FOR: the cause, and its reason.
 *
 * Frame v2.12 (template L636-651, script L935): "Sorted — write it up" is
 * the class chips, the reason chips, and ONE OPTIONAL LINE for Quality. The
 * line is not owed. What is owed is the field the whole sheet exists to
 * collect — 58.3% of three thousand historical andons closed with nothing
 * recorded, and a close button that does not ask for the cause is how that
 * number is reproduced. The Andon app's close also required a sentence
 * saying what was done; the frame drops that, because a sentence somebody is
 * made to type is "fixed" nine times out of ten, and the cause chips say
 * more in two taps.
 *
 * SAFE TO MAKE MANDATORY BECAUSE IT CAN ALWAYS BE SATISFIED, which is the
 * same test the raise applies to its own gate: every class carries a "Not
 * sure" reason and the taxonomy carries an `unknown` class, so somebody who
 * genuinely cannot tell clears it in two taps. An honest "nobody could tell"
 * and an unfilled field are different facts, and separating them is the only
 * way the unattributable rate gets measured down rather than deplored.
 *
 * WHICH MEANS THE REASON IS ONLY OWED WHERE THERE IS ONE TO PICK. "Unknown /
 * insufficient info" carries no reasons at all, so demanding a second tap
 * there would hold the button shut against a sheet showing nothing to tap.
 *
 * `order` first: a stop that restarts before it stopped is a mistake with the
 * day picker behind the More row, and it is said beside the stamps rather
 * than quietly reordered — which of the two is wrong is something only the
 * person who was there knows.
 */
export type CloseOwed = 'order' | 'cause' | 'reason' | null;

export function closeOwes(input: {
  categoryId: number | null;
  /** The class picked, with its reasons, or null while none is. */
  chosen: Pick<CauseClass, 'reasons'> | null;
  subcategoryId: number | null;
  /** The stop is declared and its two stamps read backwards. */
  backwards: boolean;
}): CloseOwed {
  if (input.backwards) return 'order';
  if (input.categoryId == null) return 'cause';
  if (input.chosen != null && input.chosen.reasons.length > 0 && input.subcategoryId == null) return 'reason';
  return null;
}

/**
 * THE SENTENCE THE RECORD GETS.
 *
 * The close posts a message from the closer, and the server insists it says
 * something — a close with an empty decision is the silent close this sheet
 * replaced. The frame's optional line is that message when it is written;
 * when it is not, the message is the cause as chosen ("Materials · Ordered
 * late"), which is what the frame's own close event reads
 * (`me + ' closed it · ' + cause + ' · ' + reason`). Never an invented
 * "fixed": the chips are what the person actually said.
 */
export function closeText(line: string, chosen: Pick<CauseClass, 'label'> | null, reason: { label: string } | null): string {
  const typed = line.trim();
  if (typed) return typed;
  return [chosen?.label, reason?.label].filter(Boolean).join(' · ');
}

/**
 * The classes worth putting in front of this write-up.
 *
 * A SHORTLIST, NEVER A FILTER. 86.7% of andons were caused somewhere other
 * than they were detected, so the class a materials andon needs is regularly
 * one nobody would have predicted from the tile — and a narrowing that hides
 * the right answer produces a wrong record, which is worse than a long list.
 * Everything is one tap away under the list (`allCauses`).
 *
 * The lens is the TILE THE OPERATOR TAPPED, and only that: the frame has no
 * "what kind of problem" chips on the sheet, so the tile is read off the
 * andon rather than offered for correction. It is never written back.
 *
 * Whatever is already CHOSEN survives the narrowing whether or not it is on
 * the shortlist. Otherwise the record's own proposal — or an answer given a
 * moment ago — would disappear the instant somebody changed the lens, which
 * reads as the sheet throwing work away.
 */
export function shortlistCauses(
  causes: CausesVM | undefined,
  lens: AlertTypeKey,
  allCauses: boolean,
  categoryId: number | null,
): CauseClass[] {
  const classes = causes?.classes ?? [];
  const keys = causes?.byType?.[lens];
  if (!keys || allCauses) return classes;
  const keep = new Set(keys);
  return classes.filter((c) => keep.has(c.key) || c.id === categoryId);
}

/**
 * The delay to SHOW: Odoo's figure until somebody moves a stamp, then a
 * wall-clock estimate of what they have moved it to.
 *
 * THE AUTHORITATIVE NUMBER IS ODOO'S, computed against the work centre's shift
 * calendar — that single choice is most of the 731× gap between ticket-hours
 * and real downtime in the history — so an untouched sheet shows exactly the
 * figure the record holds and not a subtraction of its own. A stop that ran
 * through a night is nine wall-clock hours and one working one, and the two
 * disagreeing on screen is the whole confusion this sheet exists to remove.
 *
 * Once a stamp MOVES, there is no figure to show but the subtraction: the real
 * one comes back the moment this saves, but a number that does not move under
 * a nudge reads as a control that is not working, and re-asking the server per
 * tap is a round trip nobody should wait out beside a machine. It is marked
 * STATED and captioned as provisional for exactly that reason.
 *
 * Null where the pair reads backwards. A number there would have to be either
 * the old one — a figure for a stop nobody is now asserting — or a negative,
 * and the sheet says what is wrong beside the stamps instead.
 */
export function minutesBetween(from: number | null, to: number | null, moved: boolean, measured: number): number | null {
  if (!moved || from == null || to == null) return measured;
  if (to < from) return null;
  return Math.round((to - from) / 60_000);
}

/**
 * Whether to offer "Close QA<n> too?" after the close has landed.
 *
 * Three facts, all the server's. The andon CARRIES a quality alert — the id
 * comes down on the record. The session is a personal account: closing a
 * quality alert is signed, and a PIN session is the service account, whose
 * groups say nothing about the operator behind the PIN. And the account is
 * one the server has marked as a leader, which is the same gate the quality
 * sheet's own Close button stands behind — Odoo's approver rule still decides
 * at the call, and its refusal comes back in its own words, but an offer made
 * to somebody it would refuse is the kind of button this app does not draw.
 */
export function offerCloseQuality(alert: Pick<AndonAlertVM, 'qualityAlertId'>, cap: { mode: 'user' | 'kiosk' | 'none'; leader: boolean }): boolean {
  return alert.qualityAlertId != null && cap.mode === 'user' && cap.leader;
}
